import qrcode from "qrcode-generator";

/**
 * The library's own stringToBytes truncates each UTF-16 unit to one byte, so
 * "café" and every emoji encode to something no scanner can read back. Byte
 * mode is defined over UTF-8, which is what every scanner assumes, so swap it
 * out once at module load — it is read at call time inside qr8BitByte.
 */
const encoder = new TextEncoder();
qrcode.stringToBytes = (s: string) => Array.from(encoder.encode(s));

/** Error correction: how much of the code can be damaged and still read. */
export type Ecc = "L" | "M" | "Q" | "H";

export const ECC_LEVELS: { value: Ecc; label: string; recovers: string }[] = [
  { value: "L", label: "Low", recovers: "7%" },
  { value: "M", label: "Medium", recovers: "15%" },
  { value: "Q", label: "Quartile", recovers: "25%" },
  { value: "H", label: "High", recovers: "30%" },
];

/** Alphanumeric mode's 45-character set — everything else needs byte mode. */
const ALPHANUMERIC = /^[0-9A-Z $%*+\-./:]+$/;

/**
 * The finished module grid. `size` counts modules per side (21 for a version-1
 * code, 177 for version 40); `dark(row, col)` is true where a module is drawn.
 */
export interface Matrix {
  size: number;
  dark(row: number, col: number): boolean;
}

export class TooMuchData extends Error {
  constructor() {
    super("That is more text than a QR code can hold.");
    this.name = "TooMuchData";
  }
}

/**
 * Encodes `text` into a module grid at the given error-correction level.
 *
 * Version 0 means "the smallest version this fits in". Numeric and
 * alphanumeric data are packed in their own denser modes, so a phone number
 * produces a visibly simpler code than the same digits in byte mode would.
 *
 * @throws TooMuchData when the text exceeds version 40 at this level.
 */
export function encode(text: string, ecc: Ecc): Matrix {
  const mode = /^\d+$/.test(text)
    ? "Numeric"
    : ALPHANUMERIC.test(text)
      ? "Alphanumeric"
      : "Byte";
  const qr = qrcode(0, ecc);
  qr.addData(text, mode);
  try {
    qr.make();
  } catch {
    // The library throws a bare string ('code length overflow.') for the one
    // failure a caller can act on: too much data for version 40.
    throw new TooMuchData();
  }
  const size = qr.getModuleCount();
  return { size, dark: (row, col) => qr.isDark(row, col) };
}
